import { useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface ChildInfo {
  id: number
  displayName: string
  username: string
}

interface Props {
  children: ChildInfo[]
  onComplete: () => void
  onSkip: () => void
}

export default function IncentiveStep({ children, onComplete, onSkip }: Props) {
  const [selectedChildId, setSelectedChildId] = useState(children[0]?.id || 0)
  const [type, setType] = useState<'DURATION' | 'MASTERY'>('DURATION')
  const [period, setPeriod] = useState<'DAILY' | 'WEEKLY'>('DAILY')
  const [goalValue, setGoalValue] = useState(15)
  const [rewardText, setRewardText] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const childName = children.find(c => c.id === selectedChildId)?.displayName || 'your child'
      const name = type === 'DURATION'
        ? `${goalValue} minutes of reading${period === 'WEEKLY' ? ' per week' : ''}`
        : `Master ${goalValue} spelling words${period === 'WEEKLY' ? ' per week' : ''}`
      await apiFetch('/onboarding/incentive', {
        method: 'POST',
        body: JSON.stringify({
          childId: selectedChildId,
          name,
          type,
          goalValue,
          period,
          exerciseTypes: type === 'MASTERY' ? 'SPELLING' : null,
          rewardText: rewardText || `Great job${period === 'DAILY' ? ' today' : ' this week'}, ${childName}!`,
        }),
      })
      setCreated(true)
    } catch (err: any) {
      setError(err.message || 'Failed to create goal.')
    } finally {
      setSubmitting(false)
    }
  }

  if (created) {
    const childName = children.find(c => c.id === selectedChildId)?.displayName || 'your reader'
    return (
      <div className="w-full max-w-sm mx-auto space-y-6">
        <MagnumSays variant="success">
          <p className="font-medium">Goal set!</p>
          <p className="mt-1 text-muted-foreground">
            I'll keep track of {childName}'s progress and you'll get an email when they hit their goal. Let's do this!
          </p>
        </MagnumSays>
        <div className="text-center">
          <button
            onClick={onComplete}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition"
          >
            Finish Setup
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <MagnumSays>
        <p className="font-medium">Last step — set a goal!</p>
        <p className="mt-1 text-muted-foreground">
          Pick a time or mastery target. Even 10 minutes a day builds serious reading skills. I'll track streaks and send you updates!
        </p>
      </MagnumSays>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {children.length > 1 && (
          <div>
            <label htmlFor="ob-goalChild" className="block text-sm font-medium mb-1">For which child?</label>
            <select
              id="ob-goalChild"
              value={selectedChildId}
              onChange={(e) => setSelectedChildId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {children.map(c => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Goal Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setType('DURATION'); setGoalValue(period === 'WEEKLY' ? 45 : 15) }}
              className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition ${
                type === 'DURATION' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Practice Time
            </button>
            <button
              type="button"
              onClick={() => { setType('MASTERY'); setGoalValue(period === 'WEEKLY' ? 10 : 3) }}
              className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition ${
                type === 'MASTERY' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Words Mastered
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Period</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPeriod('DAILY')
                setGoalValue(type === 'DURATION' ? 15 : 3)
              }}
              className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition ${
                period === 'DAILY' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Daily
            </button>
            <button
              type="button"
              onClick={() => {
                setPeriod('WEEKLY')
                setGoalValue(type === 'DURATION' ? 45 : 10)
              }}
              className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition ${
                period === 'WEEKLY' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Weekly
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="ob-goalValue" className="block text-sm font-medium mb-1">
            {period === 'DAILY' ? 'Daily' : 'Weekly'} Target:{' '}
            <strong>
              {goalValue} {type === 'DURATION' ? 'minutes' : 'words'}
            </strong>
          </label>
          <input
            id="ob-goalValue"
            type="range"
            min={type === 'DURATION' ? (period === 'WEEKLY' ? 15 : 5) : (period === 'WEEKLY' ? 3 : 1)}
            max={type === 'DURATION' ? (period === 'WEEKLY' ? 300 : 60) : (period === 'WEEKLY' ? 50 : 15)}
            step={type === 'DURATION' ? 5 : 1}
            value={goalValue}
            onChange={(e) => setGoalValue(Number(e.target.value))}
            className="w-full accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>
              {type === 'DURATION'
                ? `${period === 'WEEKLY' ? '15' : '5'} min`
                : `${period === 'WEEKLY' ? '3' : '1'}`}
            </span>
            <span>
              {type === 'DURATION'
                ? `${period === 'WEEKLY' ? '300' : '60'} min`
                : `${period === 'WEEKLY' ? '50' : '15'}`}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="ob-reward" className="block text-sm font-medium mb-1">
            Reward Message <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            id="ob-reward"
            type="text"
            value={rewardText}
            onChange={(e) => setRewardText(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="e.g., Great job! You earned 30 minutes of screen time"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? 'Setting goal...' : 'Set Goal'}
        </button>
      </form>

      <button
        onClick={onSkip}
        className="w-full py-2.5 border border-border rounded-lg font-medium text-muted-foreground hover:text-foreground transition"
      >
        Skip for now
      </button>
      <p className="text-xs text-center text-muted-foreground">You can set up goals later in your dashboard.</p>
    </div>
  )
}
